import React from "react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import CollectionCard from "../components/CollectionCard";
import LogoSlider from "../components/LogoSlider";
import Seo, { SITE } from "../components/Seo";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { PRODUCT_LINES_L } from "../content";
import { MATCH, MATCH_SPECS } from "../matchCollection";
import { SEO_META } from "../seo";

export default function Products() {
  const { t, loc } = useI18n();
  const { collections, content } = useSiteData();
  const master = collections.find((c) => c.key === "match");
  const fallbackPdf = master?.pdf_url || "";
  const pages = content.pages || {};
  const txt = (k, f) => loc(pages[k]) || t(f);
  const mainCategories = collections.filter((c) => !c.parent);
  const childrenOf = (key) => collections.filter((c) => c.parent === key);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "MATCH Collection 2026",
    itemListElement: collections.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: c.name,
        description: loc(c.character),
        brand: { "@type": "Brand", name: "HRD Architecture" },
        material: "CNC routed 6 mm MDF",
        url: `${SITE}/products`,
      },
    })),
  };

  return (
    <div>
      <Seo
        path="/products"
        title={loc(SEO_META.products.title)}
        description={loc(SEO_META.products.description)}
        type="product"
        jsonLd={jsonLd}
      />
      <PageHeader
        index="[ 02 // PRODUCTS ]"
        title={<span className="whitespace-pre-line">{txt("productsTitle", "products.title")}</span>}
        lead={txt("productsLead", "products.lead")}
      />

      {/* ---------- COLLECTIONS ---------- */}
      <section className="shell py-16 lg:py-24">
        <p className="mono text-[9.5px] text-[#6B675F] mb-10">{txt("productsCollectionsLabel", "products.collections")}</p>
        <div className="flex flex-col gap-6 lg:gap-8">
          {mainCategories.map((c, i) => (
            <Reveal key={c.id} delay={(i % 2) * 70}>
              <CollectionCard
                item={c}
                index={i}
                fallbackPdf={fallbackPdf}
                subCount={childrenOf(c.key).length}
              />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- SHARED SPECIFICATION ---------- */}
      <section className="bg-[#EFECE6] border-t border-b border-[#E2E0D8] py-16 lg:py-20">
        <div className="shell grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
          {MATCH_SPECS.map(([k, v]) => (
            <div key={k} className="bg-[#FAFAFA] p-5 lg:p-7">
              <p className="mono text-[8.5px] text-[#A8A29A]">{k}</p>
              <p className="text-[13px] lg:text-[14px] text-[#141414] mt-2.5 leading-snug">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- THREE PRODUCT LINES ---------- */}
      <section className="shell py-20 lg:py-28">
        <p className="mono text-[9.5px] text-[#6B675F] mb-10">{txt("productsLinesLabel", "products.lines")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PRODUCT_LINES_L.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <div className="bg-white border border-[#E2E0D8] h-full group">
                <div className="media aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]"
                  />
                </div>
                <div className="p-7">
                  <span className="mono text-[9px] text-[#6B675F]">{loc(p.family)}</span>
                  <h3 className="display text-[24px] lg:text-[28px] text-[#141414] mt-3 leading-tight">{p.name}</h3>
                  <p className="text-[13.5px] text-[#141414]/65 mt-4 leading-relaxed">{loc(p.text)}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {p.specs.map((s) => (
                      <span key={s} className="mono text-[8.5px] border border-[#E2E0D8] px-2.5 py-1.5 text-[#141414]/70">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- CLOSING ---------- */}
      <section className="bg-[#EFECE6] border-t border-[#E2E0D8]">
        <div className="shell py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 border border-[#E2E0D8] bg-white p-4">
            <img src={MATCH.closingImage} alt="MATCH Collection" className="w-full object-contain" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="serif-quote text-[28px] sm:text-[38px] lg:text-[46px] text-[#141414] leading-[1.1]">
              {MATCH.closingLine}
            </p>
            <p className="mono text-[9.5px] text-[#6B675F] mt-8">KAARI · HAVU · VIRTA · KEHÄ</p>
          </div>
        </div>
      </section>
      <LogoSlider page="products" anchor="bottom" />
    </div>
  );
}
